import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import {
    AttachmentHandler,
    DateRange,
    FlyFreelyError,
    FlyFreelyLoggingService,
    GqlSortField,
    NoticeDto,
    PersonsOrganisationDto,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { ColumnSortPreferences } from '@flyfreely-portal-ui/flyfreely-table';
import {
    CurrentOrganisation,
    WorkspaceStateService
} from '@flyfreely-portal-ui/workspace';
import {
    DisplayNotice,
    NoticeAudience,
    NoticeImportance,
    NoticesService,
    UpdateNoticeCommand
} from 'libs/flyfreely/src/lib/services/notices.service';
import {
    BehaviorSubject,
    ReplaySubject,
    Subject,
    catchError,
    combineLatest,
    throwError
} from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import Audience = NoticeDto.Audience;

export const formatLotamPriority = {
    [NoticeImportance.LOW]: 'Low',
    [NoticeImportance.MEDIUM]: 'Medium',
    [NoticeImportance.HIGH]: 'High'
};

export const formatLotamAudience = {
    [Audience.GENERAL]: 'General',
    [Audience.FLIGHT_OPERATIONS]: 'Flight Operations'
};

export const formatOperatingRequirement: Record<string, string> = {
    NONE: 'None',
    ACKNOWLEDGE: 'Acknowledge',
    BLOCK: 'Block'
};

@Injectable()
export class LotamDataService implements OnDestroy {
    currentOrganisation: PersonsOrganisationDto;

    private lotamSource = new ReplaySubject<DisplayNotice[]>(1);
    private workingSource = new BehaviorSubject<boolean>(false);
    private totalItemsSource = new ReplaySubject<number>(1);
    private currentPageSource = new BehaviorSubject<number>(0);

    private searchCriteriaSubject = new ReplaySubject<{
        page: number;
        limit: number;
        organisationId: number;
        sorting?: ColumnSortPreferences;
        date?: DateRange;
        subject?: string;
        description?: string;
        importance?: NoticeImportance;
        acknowledged?: boolean;
        text?: string;
        noticeTypeId?: number;
    }>(1);

    working$ = this.workingSource.asObservable();
    lotamSource$ = this.lotamSource.asObservable();
    currentPage$ = this.currentPageSource.asObservable();
    totalItems$ = this.totalItemsSource.asObservable();
    searchCriteria$ = this.searchCriteriaSubject.asObservable();

    private workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private workspaceStateService: WorkspaceStateService,
        private logging: FlyFreelyLoggingService,
        private http: HttpClient,
        private noticesService: NoticesService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => this.workingSource.next(working));

        combineLatest([
            this.workspaceStateService.currentOrganisation$.pipe(
                filter(
                    organisation => organisation.type === 'organisation_loaded'
                )
            ),
            this.searchCriteriaSubject
        ]).subscribe(([organisation, criteria]) => {
            this.currentOrganisation = (
                organisation as CurrentOrganisation
            ).organisation;
            if (this.currentOrganisation != null) {
                this.updateLotamsData(criteria);
            } else {
                this.lotamSource.next([]);
            }
        });
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.workingSource.complete();
        this.searchCriteriaSubject.complete();
    }

    findLotams({
        page,
        limit,
        organisationId,
        sorting,
        date,
        subject,
        description,
        importance,
        acknowledged,
        text,
        noticeTypeId
    }: {
        page: number;
        limit: number;
        organisationId: number;
        sorting?: ColumnSortPreferences;
        date?: DateRange;
        subject?: string;
        description?: string;
        importance?: NoticeImportance;
        acknowledged?: boolean;
        text?: string;
        noticeTypeId?: number;
    }) {
        this.searchCriteriaSubject.next({
            organisationId,
            page,
            limit,
            sorting,
            date,
            subject,
            description,
            importance,
            acknowledged,
            text,
            noticeTypeId
        });
    }

    private updateLotamsData({
        organisationId,
        page,
        limit,
        sorting,
        date,
        subject,
        description,
        importance,
        acknowledged,
        text,
        noticeTypeId
    }: {
        organisationId: number;
        page: number;
        limit: number;
        sorting?: ColumnSortPreferences;
        date?: DateRange;
        subject?: string;
        description?: string;
        importance?: NoticeImportance;
        acknowledged?: boolean;
        text?: string;
        noticeTypeId?: number;
    }) {
        const doneWorking = this.workTracker.createTracker();
        const sortFieldMap: Record<string, string> = {
            'noticeType.name': 'noticeType'
        };
        const sortFields: GqlSortField = {
            field: sortFieldMap[sorting?.column] ?? sorting?.column ?? null,
            order: sorting?.ascending ? 'ASC' : 'DESC'
        };
        this.noticesService
            .findNotices(
                organisationId,
                {
                    page: page,
                    pageSize: limit,
                    sortFields: sorting != null ? [sortFields] : []
                },
                date,
                subject,
                description,
                importance,
                acknowledged,
                text,
                'FLIGHT_OPERATIONS',
                noticeTypeId
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: notices => {
                    this.totalItemsSource.next(notices.count);
                    this.currentPageSource.next(page);
                    this.lotamSource.next(notices.results);
                    doneWorking();
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(error);
                    this.lotamSource.next([]);
                    this.totalItemsSource.next(0);
                    this.currentPageSource.next(0);
                    doneWorking();
                }
            });
    }

    attachmentHandler(noticeId: number, organisationId: number) {
        return new AttachmentHandler(
            this.http,
            `/webapi/notices/${noticeId}/attachments`,
            false,
            organisationId
        );
    }

    newLotam(organisationId: number, audience: NoticeAudience) {
        return this.noticesService.newNotice(organisationId, audience);
    }

    deleteLotam(lotamId: number) {
        return this.noticesService.deleteNotice(lotamId);
    }

    updateLotam(command: UpdateNoticeCommand) {
        return this.noticesService.updateNotice(command);
    }

    findLotamById(lotamId: number, organisationId: number) {
        return this.noticesService.findNoticeById(lotamId, organisationId).pipe(
            catchError((error: FlyFreelyError) => {
                this.logging.error(
                    error,
                    `Error while findLotamById: ${error.message}`
                );
                return throwError(() => error.message ?? 'Entry is invalid');
            })
        );
    }

    previewLotamDisplayLocation(command: UpdateNoticeCommand) {
        return this.noticesService.previewNoticeDisplayLocation(command);
    }

    acknowledgeLotam(lotamId: number) {
        return this.noticesService.acknowledgeNotice(lotamId);
    }

    acknowledgeMissionLotam(missionId: number, noticeId: number) {
        return this.noticesService.acknowledgeMissionNotice(
            missionId,
            noticeId
        );
    }
}
